import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { WorkSessionDetail } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { MapUnmappedTimeDialog } from '@/components/MapUnmappedTimeDialog';
import { BackfillDayCard } from '@/components/BackfillDayCard';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export const workLogHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/work-log/history',
  component: WorkLogHistoryPage,
});

function statusLabel(status: string): string {
  return status === 'auto_closed' ? 'auto-closed' : status;
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function durationLabel(minutes: number): string {
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

function minutesBetween(fromIso: string, toIso: string): number {
  return Math.max(0, Math.floor((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60_000));
}

function deadlineLabel(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function WorkLogHistoryPage(): JSX.Element {
  const sessionsQuery = useQuery({
    queryKey: ['work-log', 'sessions'],
    queryFn: () => api.listWorkSessions({}),
  });

  // A corrected session is superseded, not deleted: hard rule 4 keeps the row
  // and the API still returns it, but the employee reading their own history
  // wants the day as it now stands, not both versions of it.
  const sessions = (sessionsQuery.data ?? []).filter((s) => s.supersededById === null);
  const [mapping, setMapping] = React.useState<WorkSessionDetail | null>(null);

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-4xl space-y-8">
        <div>
          <h1 className="font-display text-4xl font-semibold md:text-5xl">Work log history</h1>
          <p className="mt-3 max-w-2xl text-lg leading-relaxed text-[hsl(var(--muted-foreground))]">
            Each day shows as it currently stands. Correcting a session replaces the entry here,
            and the original is kept on file.
          </p>
        </div>

        <BackfillDayCard />

        {sessions.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>No sessions yet</CardTitle>
              <CardDescription>Clock in from the work log to record your first day.</CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => (
              <Card key={s.id} id={s.id} className="scroll-mt-24">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">{s.businessDate}</CardTitle>
                    <Badge variant={s.status === 'auto_closed' ? 'destructive' : 'secondary'}>
                      {statusLabel(s.status)}
                    </Badge>
                    {s.adjustsSessionId ? <Badge variant="outline">adjustment</Badge> : null}
                    <span className="ml-auto text-sm tabular-nums">
                      <span className="font-medium">{durationLabel(s.workedMinutes)}</span>
                      <span className="text-[hsl(var(--muted-foreground))]"> worked</span>
                    </span>
                  </div>
                  {s.adjustmentReason ? (
                    <CardDescription>{s.adjustmentReason}</CardDescription>
                  ) : null}
                </CardHeader>
                <CardContent className="space-y-1 pt-0 text-sm">
                  <dl className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 rounded-xl border border-[hsl(var(--border))] p-3 text-sm sm:grid-cols-4">
                    <div>
                      <dt className="text-xs text-[hsl(var(--muted-foreground))]">Clock in</dt>
                      <dd className="tabular-nums">{timeLabel(s.clockInAt)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-[hsl(var(--muted-foreground))]">Clock out</dt>
                      <dd className="tabular-nums">
                        {s.clockOutAt ? timeLabel(s.clockOutAt) : <span className="text-[hsl(var(--muted-foreground))]">still clocked in</span>}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-[hsl(var(--muted-foreground))]">Clocked-in time</dt>
                      <dd className="tabular-nums">{durationLabel(s.workedMinutes + s.breakMinutes)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-[hsl(var(--muted-foreground))]">Breaks</dt>
                      <dd className="tabular-nums">
                        {s.breakMinutes > 0 ? `− ${durationLabel(s.breakMinutes)}` : 'none'}
                      </dd>
                    </div>
                    {s.breaks.length > 0 ? (
                      <div className="col-span-2 sm:col-span-4 border-t border-[hsl(var(--border))] pt-2">
                        <dt className="text-xs text-[hsl(var(--muted-foreground))]">Break details</dt>
                        <dd className="mt-1 flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
                          {s.breaks.map((b, i) => (
                            <span key={b.id}>
                              Break {i + 1}: {timeLabel(b.startedAt)} →{' '}
                              {b.endedAt ? timeLabel(b.endedAt) : 'ongoing'}
                              {b.endedAt ? (
                                <span className="text-[hsl(var(--muted-foreground))]">
                                  {' '}({minutesBetween(b.startedAt, b.endedAt)} min)
                                </span>
                              ) : null}
                            </span>
                          ))}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                  {s.splits.length === 0 ? (
                    <p className="text-[hsl(var(--muted-foreground))]">No time declared.</p>
                  ) : (
                    s.splits.map((sp) => (
                      <div key={sp.id} className="flex items-center gap-2">
                        <span className="flex-1">
                          {sp.taskTitle ?? (
                            <span className="text-[hsl(var(--muted-foreground))]">
                              Unmapped time
                            </span>
                          )}
                          {sp.projectName ? (
                            <span className="text-[hsl(var(--muted-foreground))]">
                              {' '}
                              · {sp.projectName}
                            </span>
                          ) : null}
                        </span>
                        {sp.outcome ? <Badge variant="outline">{sp.outcome}</Badge> : null}
                        <span className="tabular-nums">{sp.declaredMinutes} min</span>
                      </div>
                    ))
                  )}

                  {s.unmappedMinutes > 0 ? (
                    <div className="mt-3 flex items-center gap-3 border-t border-[hsl(var(--border))] pt-3">
                      {s.mappingWindowOpen ? (
                        <>
                          <span className="flex-1 text-[hsl(var(--muted-foreground))]">
                            Map this time by {deadlineLabel(s.mappingDeadline)}.
                          </span>
                          <Button size="sm" variant="outline" onClick={() => setMapping(s)}>
                            Map time
                          </Button>
                        </>
                      ) : (
                        <span className="flex-1 text-[hsl(var(--muted-foreground))]">
                          The window to map this time has closed. Ask your manager to map it.
                        </span>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {mapping ? (
          <MapUnmappedTimeDialog
            session={mapping}
            open={mapping !== null}
            onOpenChange={(next) => {
              if (!next) setMapping(null);
            }}
          />
        ) : null}
      </div>
    </AppShell>
  );
}
